import {observer} from "mobx-react-lite";
import React from "react";
import {useTranslation} from "react-i18next";
import {Modal, ModalBody, ModalFooter} from "reactstrap";
import {IconSource} from "../../../shared/enums/iconSource";
import ButtonIcon from "../../atoms/button/buttonIcon";
import "./inReleaseCalendarConfirmationModal.sass";
import ModalDetailHeader from "../../molecules/modalDetailHeader/modalDetailHeader";
import Flex from "../../atoms/common/flex";
import ModalDetailContent from "../../molecules/modalDetailContent/modalDetailContent";
import EventTimeAndDateFormat from "../../atoms/eventTimeAndDateFormat/eventTimeAndDateFormat";
import {isWebLink} from "../../../utils/stringUtils";
import EventDetails, {
  CustomLocationConstant,
  VirtualEventConstant
} from "../../../stores/domainStore/pages/models/eventDetails";

export interface IEventTemplateDetailModalProps {
  eventDetails: EventDetails | null;
  isOpen: boolean;
  toggle: () => void;
  onPublishBtnClicked: () => void;
  isEnhanced: boolean;
  isSaving?: boolean;
}
const EventTemplateDetailModal: React.FC<IEventTemplateDetailModalProps> = ({
  eventDetails,
  toggle,
  isSaving,
  isOpen,
  isEnhanced,
  onPublishBtnClicked
}) => {
  const {t} = useTranslation(["translation", "event"]);

  return (
    <div className="pre-release-modal">
      {eventDetails && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          modalClassName="event-detail-modal"
          centered
          data-testid="publishToReleasedCalendarModal"
        >
          <ModalDetailHeader isLoading={false} toggle={toggle}>
            <div className="header-container">
              <div className="header-column column-dynamic">
                {t("translation:titles.confirmPublishToReleaseCalendar")}
              </div>
            </div>
          </ModalDetailHeader>

          <ModalBody>
            <div className="in-release-calendar-body">
              {t("translation:messages.confirmPublishToReleaseCalendar")}
            </div>
            <div
              className="details-heading"
              data-testid="eventDetailModalHeading"
            >
              {eventDetails.title.text}
            </div>
            <ModalDetailContent icon={IconSource.recent}>
              <EventTimeAndDateFormat
                timeAndDate={eventDetails.eventDateDetails.startDate.date}
                endTime={
                  isEnhanced ? eventDetails.eventDateDetails.endDate.date : null
                }
                timeZone={eventDetails.eventDateDetails.timeZone}
              />
            </ModalDetailContent>

            {isEnhanced && (
              <ModalDetailContent icon={IconSource.location}>
                {eventDetails.location.selectedItem?.value ===
                  VirtualEventConstant &&
                isWebLink(eventDetails.virtualLocation.text) ? (
                  <a
                    href={eventDetails.virtualLocation.text}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("event:labels.virtualEventSelect")}
                  </a>
                ) : eventDetails.location.selectedItem?.value ===
                  CustomLocationConstant ? (
                  <>{eventDetails.customLocation.text}</>
                ) : (
                  <>{eventDetails.location.selectedItem?.label}</>
                )}
              </ModalDetailContent>
            )}
          </ModalBody>

          <ModalFooter tag={Flex} className={"event-publish-modal-footer"}>
            <div className="event-publish-modal-buttons">
              <div className="in-release-calendar-dont-publish-button">
                <ButtonIcon type="secondary" onClick={toggle}>
                  {t("translation:actions.dontPublish")}
                </ButtonIcon>
              </div>
            </div>
            <ButtonIcon
              type={"primary"}
              onClick={onPublishBtnClicked}
              isDisabled={isSaving}
            >
              {t("translation:confirmations.yesPublish")}
            </ButtonIcon>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default observer(EventTemplateDetailModal);
